import React, { useState, useMemo, useCallback, useEffect } from 'react';
import styles from './CoverLesson.module.scss';
import { ClockCircleOutlined } from '@ant-design/icons';
import { formatDate } from '@src/utils/time';
import { LordIcon } from '@src/components/lord-icon/lord-icon';
import {
  IconSkill,
  IconExercises,
  IconComforts,
  IconMoods,
  IconStructures,
  categorySkillsOptions,
  categoryStructureOptions,
  categoryComfortLevelOptions,
  categoryMoodOptions1,
  categoryExerciseStyleOptions,
  IconLesson,
  IconInfo,
 } from '@src/constants';
import { SllLesson } from '@src/api/graphql-codegen';
import { getMediaAbsolutePath } from '@src/services/File';
import ImageGrade from '@src/assets/img/grade_hat.png';
import { DraggableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd';
import { useMediaQuery } from 'react-responsive';
import useDragScrolling from '../customize-units/card/useDragScrolling';
import { useDrag } from 'react-dnd';
import IconRemovUnit from 'src/assets/icons/remove_unit.svg?react';
import IconPencil from 'src/assets/icons/pencil.svg?react';
import Tooltip from '../tooltip/Tooltip';
import { LazyLoadImage } from 'react-lazy-load-image-component';

type Props = {
  lesson: SllLesson;
  type?: string;
  source?: string;
  target?: string;
  // isDragable: boolean
  handleLeftArrowAction?: (lesson: SllLesson) => void
  handleRightArrowAction?: (lesson: SllLesson) => void
  handleOpenLesson?: (lesson: SllLesson) => void
  draggableProvided?: DraggableProvided;
  snapshot?: DraggableStateSnapshot;
  studyMode?: string
  handleMovableLesson?: (movable: boolean) => void
  showLabel?: boolean
  cardHovered?: boolean
  hoveredLessonId?: string | ""
  // index: number
  handleCardHovered?: (lessonId: string, hovered: boolean) => void
  handleSelectedCenterCard?: (centerCard: boolean) => void
  handleMovableNewCover?: (inNew: boolean) => void;
  isEdit?: boolean;
  handleEditLesson?: (lesson: SllLesson) => void
}

function CoverLesson(props: Props) {
  const [isHovered, setIsHovered] = useState(false);
  const { lesson, type="type", source, isEdit, handleMovableNewCover, handleRightArrowAction, handleOpenLesson, handleEditLesson } = props
  const isMobile = useMediaQuery({ maxWidth: 915 });
  const {
    id, name, country, ageGroup, gradeLevel, categories, isNewCover,
    suggestedDuration, progress, coverImage, isOpReel,
    isComingSoon, comingSoonAt, isNew, isUpdated, lessonDetails,

  } = lesson;

  const unitCategories = useMemo(() => JSON.parse(categories || '[]'), [categories])
  const { addEventListenerForWindow, removeEventListenerForWindow } = useDragScrolling();

  const [{ opacity, isDragging }, drag] = useDrag(
    () => ({
      type,
      item: () => {
        // like begin drag
        addEventListenerForWindow();
        return { lesson }
      },
      end: () => {
        // like end drag
        removeEventListenerForWindow();
      },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
        opacity: !!monitor.isDragging() ? 0.4 : 1,
      }),
    }),
    [lesson, type],
  )

  const [structure, setStructure] = useState('')
  const [skill, setSkill] = useState('')
  const [comfortLevel, setComfortLevel] = useState('')
  const [mood, setMood] = useState('')
  const [exerciseStyle, setExerciseStyle] = useState('')

  useEffect(() => {
    if (isDragging) {
      handleMovableNewCover?.( isOpReel && isNewCover ? isNewCover : false)
    }
  }, [isDragging, isOpReel, isNewCover, handleMovableNewCover])

  useEffect(() => {
    if (unitCategories['Structure']) {
      setStructure(unitCategories['Structure'])
    }
    if (unitCategories['Skill']) {
      setSkill(unitCategories['Skill'])
    }
    if (unitCategories['Comfort Level']) {
      setComfortLevel(unitCategories['Comfort Level'])
    }
    if (unitCategories['Mood']) {
      setMood(unitCategories['Mood'])
    }
    if (unitCategories['Exercise Style']) {
      setExerciseStyle(unitCategories['Exercise Style'])
    }
  }, [unitCategories])

  const comingSoonDate = useMemo(() => {
    if (isComingSoon && comingSoonAt) {
      let d;
      if (comingSoonAt.includes('-')) {
        d = new Date('2023-09-23');
      } else {
        d = new Date(parseInt(comingSoonAt));
      }
      var dateString = formatDate(d.valueOf(), 'MMM dd, yyyy')
      return dateString;
    } else {
      return ''
    }
  }, [isComingSoon, comingSoonAt])

  const onClickUnit = useCallback(() => {
    handleOpenLesson?.(lesson)
  }, [handleOpenLesson, lesson])

  const actionContent = useMemo(() => {
    switch (source) {
      case 'grade': {
        return <div className={styles.iconWrapper}>
          <Tooltip position={'right'} tooltip={'Remove Content'}>
            <IconRemovUnit onClick={() => handleRightArrowAction && handleRightArrowAction(lesson)} className={styles.arrowLeftIcon}/>
          </Tooltip>
        </div>
      }
      case 'addition': {
        return <div className={styles.iconWrapper}>
          <Tooltip position={'right'} tooltip={'Remove Content'}>
            <IconRemovUnit onClick={() => handleRightArrowAction && handleRightArrowAction(lesson)} className={styles.arrowLeftIcon}/>
          </Tooltip>
        </div>
      }
      default: {
        return <>
        </>
      }
    }
  }, [lesson, source, handleRightArrowAction]);

  const label = useMemo(() => {
    if (isNew) {
      return (
        <div className={styles.labelContainer}>NEW</div>
      )
    } else if (isUpdated) {
      return (
        <div className={styles.labelContainer}>UPDATED</div>
      )
    } else if (isComingSoon) {
      return (
        <div className={styles.labelContainer}>UPDATE COMING {comingSoonDate}</div>
      )
    }
  }, [isNew, isUpdated, isComingSoon, comingSoonDate])

  const gradeBar = (
    <div className={styles.gradeBar}>
      <img src={ImageGrade} alt='grade' />
      <span>
        { country === 'Australia' ? (ageGroup?.includes('[') ? JSON.parse(ageGroup).join(', ') : ageGroup) : gradeLevel }
      </span>
    </div>
  )

  const progressBar = (
    <div className={styles.progressBackBar} style={{borderBottomLeftRadius: isNewCover ? '0' : '10px', borderBottomRightRadius: isNewCover ? '0' : '10px'}}>
      <div className={styles.progressBar} style={{width: `${progress}%`, backgroundColor: progress === 100 ? "#62C590" : "#62C590", borderBottomLeftRadius: isNewCover ? '0' : '10px', borderBottomRightRadius: isNewCover ? '0' : progress === 100 ? '10px' : '0' }}>
      </div>
    </div>
  )

  const suggestBar = (
    <div className={styles.suggestView}>
      {/* <div className={styles.lessonsView}>
        <LordIcon src={IconLesson} isHover={true} colors={{ primary: "#FFF", secondary: "#FFF" }} size={16}/>
        <div className={styles.lessonsText}>{suggestedLessonCount && suggestedLessonCount > 1 ? `${suggestedLessonCount} Lessons` : `${suggestedLessonCount} Lesson`}</div>
      </div> */}
      <div className={styles.durationView}>
        <ClockCircleOutlined className={styles.clockIcon}/>
        <div className={styles.durationText}>{`${suggestedDuration}`}&nbsp;<span>min</span></div>
      </div>
    </div>
  )

  const bottomView = (
    <div className={styles.bottomView}>
      <div className={styles.unitName}>{name}</div>
      {skill ? <div className={[styles.tagBox, styles['skill']].join(' ')}>
        <LordIcon src={IconSkill[categorySkillsOptions.findIndex(o => o.value === skill)]}
          isHover={true}
          colors={{
            primary: '#274649',
            secondary: '#FFB073'
          }}
          size={isMobile ? 10: 16}
          target='.CoverUnit_tagBox__nusN2' />
        <span>{skill}</span>
      </div> : null}
      <div className={styles.divider} />
      <div className={styles.categoryView}>
      {mood ? <div className={[styles.tagBox, styles['mood']].join(' ')}>
        <LordIcon src={IconMoods[categoryMoodOptions1.findIndex(o => o.value === mood)]}
          isHover={true}
          colors={{
            primary: '#274649',
            secondary: '#FFB073'
          }}
          size={isMobile ? 10: 16}
          target='.CoverUnit_tagBox__nusN2' />
          <span>{mood}</span></div> : null}
      {comfortLevel ? <div className={[styles.tagBox, styles['comfortLevel']].join(' ')}>
        <LordIcon src={IconComforts[categoryComfortLevelOptions.findIndex(o => o.value === comfortLevel)]}
          isHover={true}
          colors={{
            primary: '#274649',
            secondary: '#FFB073'
          }}
          size={isMobile ? 10: 16}
          target='.CoverUnit_tagBox__nusN2' />
        <span>{comfortLevel}</span></div> : null}
      {structure ? <div className={[styles.tagBox, styles['structure']].join(' ')}>
        <LordIcon src={IconStructures[categoryStructureOptions.findIndex(o => o.value === structure)]}
          isHover={true}
          colors={{
            primary: '#274649',
            secondary: '#FFB073'
          }}
          size={isMobile ? 10: 16}
          target='.CoverUnit_tagBox__nusN2' />
        <span>{structure}</span></div> : null}
      {exerciseStyle ? <div className={[styles.tagBox, styles['exerciseStyle']].join(' ')}>
        <LordIcon src={IconExercises[categoryExerciseStyleOptions.findIndex(o => o.value === exerciseStyle)]}
          isHover={true}
          colors={{
            primary: '#274649',
            secondary: '#FFB073'
          }}
          size={isMobile ? 10: 16}
          target='.CoverUnit_tagBox__nusN2' />
          <span>{exerciseStyle}</span></div> : null}
      </div>
    </div>
  )

  const bottomOnboardingView = (
    <div className={styles.bottomView}>
      <div className={styles.unitName}>{name}</div>
      <div className={styles.divider} />
      <div className={styles.onboardingDetailView}>
        <LordIcon src={IconInfo}
          isHover={true} 
          colors={{
            primary: '#AF77B8',
            secondary: '#FFB073'
          }}
          size={isMobile ? 10: 20}
          target='.CoverUnit_tagBox__nusN2' />
        <span>{lessonDetails}</span>
      </div>
    </div>
  )

  return (
    <div ref={drag} id={`${id}`} className={[isHovered ? styles.newCoverHoveredCard : styles.newCoverCard, source === 'grade' ? styles.innerShadow : ''].join(' ')}
      style={{height: !isNewCover ? 'min-content' : '', opacity, cursor: isDragging ? 'grabbing' : 'grab'}}
      onClick={onClickUnit} onMouseOver={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      <div className={styles.topView}>
        <LazyLoadImage src={`${coverImage ? getMediaAbsolutePath(coverImage) : ''}`} className={isNewCover ? styles.roundImage : styles.image} alt="cover"></LazyLoadImage>
        {label}
        {(progress && progress > 0) ? progressBar : null}
        {isNewCover ? suggestBar : null}
        {isNewCover ? gradeBar : null}
        {actionContent}
        {isEdit ?       <div className={styles.iconView}>
        <div className={styles.editIconWrapper}>
          <IconPencil onClick={() => handleEditLesson && handleEditLesson(lesson)}/>
        </div>
      </div> : null}
      </div>
      {isNewCover ? bottomView : null}
    </div>
  );
}

export default CoverLesson

